import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertCircle, Check, X, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Toast Minimalista
const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(onClose, 300);
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  return (
    <div className={`fixed top-6 right-6 z-50 ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
      <div className="bg-white border border-ink-100 rounded-xl shadow-lifted overflow-hidden min-w-[320px]">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className={`
            w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
            ${type === 'success' ? 'bg-emerald-50' : 'bg-red-50'}
          `}>
            {type === 'success' ? (
              <Check size={16} className="text-emerald-600" />
            ) : (
              <X size={16} className="text-red-600" />
            )}
          </div>
          <p className="text-sm text-ink-700 flex-1">{message}</p>
          <button
            onClick={() => {
              setIsClosing(true);
              setTimeout(onClose, 300);
            }}
            className="text-ink-300 hover:text-ink-500 transition-colors p-1"
          >
            <X size={16} />
          </button>
        </div>
        <div className="h-0.5 bg-ink-50">
          <div
            className={`h-full transition-all duration-100 ease-linear ${type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

function Login({ onLogin, logoutMessage, onClearMessage }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [authStep, setAuthStep] = useState('');
  const [intentosFallidos, setIntentosFallidos] = useState(0);
  const [bloqueadoHasta, setBloqueadoHasta] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  // Mostrar mensaje de logout por inactividad si existe
  useEffect(() => {
    if (logoutMessage) {
      showToast(logoutMessage, 'error');
      if (onClearMessage) {
        onClearMessage();
      }
    }
  }, [logoutMessage, onClearMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verificar bloqueo temporal
    if (bloqueadoHasta && new Date() < bloqueadoHasta) {
      const segundos = Math.ceil((bloqueadoHasta - new Date()) / 1000);
      setError(`Demasiados intentos. Espera ${segundos} segundos.`);
      return;
    }

    setError('');
    setLoading(true);
    setAuthStep('Autenticando...');

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        setAuthStep('Acceso concedido');
        showToast('Inicio de sesión exitoso', 'success');
        setIntentosFallidos(0);
        setBloqueadoHasta(null);

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.usuario));

        setTimeout(() => {
          onLogin(data.usuario);
        }, 1000);
      } else {
        const nuevosIntentos = intentosFallidos + 1;
        setIntentosFallidos(nuevosIntentos);
        if (nuevosIntentos >= 3) {
          setBloqueadoHasta(new Date(Date.now() + 30000));
          setIntentosFallidos(0);
          setError('Demasiados intentos. Espera 30 segundos.');
        } else {
          setError('Credenciales inválidas');
        }
        showToast('Credenciales inválidas', 'error');
        setAuthStep('');
      }
    } catch (error) {
      console.error('Error de conexión:', error);
      setError('Error al conectar con el servidor');
      showToast('Error de conexión con el servidor', 'error');
      setAuthStep('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-12">
            <img
              src="/images/LogotipoDragonCargoWhite.png"
              alt="Dragon Cargo"
              className="max-h-12 w-auto object-contain invert opacity-90"
              style={{ maxWidth: '180px' }}
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl text-ink-900 mb-2">
              Bienvenido
            </h1>
            <p className="text-ink-400 text-sm">
              Ingresa tus credenciales para continuar
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3 animate-fade-in">
              <AlertCircle className="text-red-500 flex-shrink-0" size={18} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Auth Progress */}
          {loading && authStep && (
            <div className="mb-6 p-3 bg-ink-50 border border-ink-100 rounded-lg animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin" />
                <p className="text-sm text-ink-600">{authStep}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-ink-500 uppercase tracking-wider mb-2">
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="
                  w-full px-4 py-3
                  bg-white border border-ink-200 rounded-lg
                  text-ink-800 placeholder-ink-300
                  focus:outline-none focus:border-ink-400 focus:ring-0
                  disabled:bg-ink-50 disabled:text-ink-400
                  transition-colors duration-200
                "
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-500 uppercase tracking-wider mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  className="
                    w-full px-4 py-3 pr-12
                    bg-white border border-ink-200 rounded-lg
                    text-ink-800 placeholder-ink-300
                    focus:outline-none focus:border-ink-400 focus:ring-0
                    disabled:bg-ink-50 disabled:text-ink-400
                    transition-colors duration-200
                  "
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-400 hover:text-ink-600 disabled:opacity-50 transition-colors"
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="
                w-full flex items-center justify-center gap-2
                px-6 py-3
                bg-ink-900 text-white rounded-lg
                hover:bg-ink-800
                disabled:bg-ink-300 disabled:cursor-not-allowed
                transition-all duration-200
                group
              "
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="text-sm font-medium">Autenticando</span>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium">Iniciar sesión</span>
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-ink-900 items-center justify-center p-12 relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-md text-center">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 bg-accent rounded-lg" />
            </div>
          </div>

          <h2 className="font-display text-4xl text-white mb-4">
            Sistema de Gestión Logística
          </h2>
          <p className="text-ink-400 text-lg leading-relaxed">
            Control integral de contenedores y demoras
          </p>

          {/* Stats */}
          <div className="mt-12 flex justify-center gap-12">
            <div>
              <p className="text-3xl font-display text-white">99.9%</p>
              <p className="text-xs text-ink-500 uppercase tracking-wider mt-1">Uptime</p>
            </div>
            <div>
              <p className="text-3xl font-display text-white">24/7</p>
              <p className="text-xs text-ink-500 uppercase tracking-wider mt-1">Monitoreo</p>
            </div>
            <div>
              <p className="text-3xl font-display text-white">100+</p>
              <p className="text-xs text-ink-500 uppercase tracking-wider mt-1">Clientes</p>
            </div>
          </div>
        </div>

        {/* Decorative accent */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-50" />
      </div>
    </div>
  );
}

export default Login;
